/**
 * 4-step wizard for creating/editing a scheduled delivery (Plan F, F0).
 *
 * Replaces the flat form that asked for a raw cron in monospace and a
 * secret-reference pointer — the two fields nobody outside engineering
 * could fill. What changed, and what deliberately did NOT:
 *
 * 1. **The schedule is picked in plain language** (daily / weekly /
 *    monthly + a time picker) and the same 5-field `cron` string as
 *    always is generated from it. The scheduler, the API and the data
 *    model are untouched; the raw cron survives under "Advanced" for
 *    whoever wants it, and a stored cron that fits no preset round-trips
 *    as "custom" showing the raw string — never silently rewritten.
 * 2. **The timezone is chosen from a human-named list**, defaulting to
 *    the admin's browser zone for new subscriptions.
 * 3. **`secret_ref` left the screen.** The credential is picked from the
 *    Integrations list (Plan C 2.3) — a pointer is still never a value,
 *    but now it is also never TYPED. A pre-033 row that only carries a
 *    ref keeps working: the ref rides along invisibly and the picker
 *    says "operator-configured (legacy)".
 * 4. **Engineering knobs live under "Advanced"** (max content age,
 *    on-missing-content, raw cron) with the same safe defaults as ever.
 *
 * The final step shows the schedule as a sentence and offers "Save &
 * test" — the existing dry-run endpoint that resolves credential +
 * content + render and sends NOTHING (a 424 here is the feature
 * working). Testing needs a saved row, so "Save & test" saves first and
 * keeps the wizard open on the result.
 */

import { Badge, Button, Card, CardBody, Select } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FlaskConical,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useT } from "@/i18n";
import {
  deliveryApi,
  type DeliveryChannel,
  type DeliveryTestResult,
} from "@/lib/builder-api";
import type { ChannelCredential } from "@/lib/integrations-api";
import {
  CHANNELS,
  LOCALES,
  REQUIRES_SECRET,
  WEEKDAY_ORDER,
  cronFromSchedule,
  formErrors,
  isSecretRefValid,
  scheduleErrors,
  scheduleFromCron,
  timezoneLabel,
  toInput,
  withChannel,
  TIMEZONES,
  type FormState,
  type ScheduleState,
} from "@/pages/delivery/delivery-form";
import { summarizeSchedule } from "@/pages/delivery/schedule-summary";
import { TeamsWorkflowGuide } from "@/pages/delivery/TeamsWorkflowGuide";

export interface DeliveryWizardProps {
  projectSlug: string;
  reports: { key: string; name: string }[];
  credentials: ChannelCredential[];
  initial: FormState;
  onClose: () => void;
  /** Called after a successful save (with or without test). The panel
   * reloads its list; the wizard may stay open showing a test result. */
  onSaved: () => void;
}

const STEP_KEYS = [
  "delivery.wizard.step.what",
  "delivery.wizard.step.who",
  "delivery.wizard.step.when",
  "delivery.wizard.step.review",
] as const;

/** Which `formErrors`/`scheduleErrors` keys gate each step's "Next". */
const STEP_ERROR_KEYS: string[][] = [
  ["name", "report"],
  ["url", "recipients", "channel", "credential"],
  ["cron", "timezone", "time", "weekdays", "monthDay"],
  [],
];

const inputCls = "w-full rounded border border-neutral-300 px-2 py-1";

export function DeliveryWizard({
  projectSlug,
  reports,
  credentials,
  initial,
  onClose,
  onSaved,
}: DeliveryWizardProps): JSX.Element {
  const { t, locale } = useT();
  const [form, setForm] = useState<FormState>(initial);
  const [schedule, setSchedule] = useState<ScheduleState>(() =>
    scheduleFromCron(initial.cron),
  );
  const [step, setStep] = useState(0);
  const [advancedOpen, setAdvancedOpen] = useState(
    () => scheduleFromCron(initial.cron).kind === "custom",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<DeliveryTestResult | null>(
    null,
  );
  /** Set once the row is saved but the follow-up test failed — the
   * message must say "saved, but…" rather than pretend nothing landed. */
  const [savedButTestFailed, setSavedButTestFailed] = useState(false);

  const patch = (next: Partial<FormState>): void =>
    setForm((f) => ({ ...f, ...next }));

  /** Every schedule change immediately re-serialises the cron string —
   * the form's `cron` field stays the single thing that gets saved. */
  const patchSchedule = (next: Partial<ScheduleState>): void =>
    setSchedule((s) => {
      const merged = { ...s, ...next };
      setForm((f) => ({ ...f, cron: cronFromSchedule(merged) }));
      return merged;
    });

  const errors = useMemo(
    () => [...formErrors(form), ...scheduleErrors(schedule)],
    [form, schedule],
  );
  const stepValid = (i: number): boolean =>
    STEP_ERROR_KEYS[i].every((k) => !errors.includes(k));
  const canSave = errors.length === 0 && !saving;

  const changeChannel = (channel: DeliveryChannel): void =>
    setForm((f) => withChannel(f, channel));

  const channelCredentials = credentials.filter(
    (c) => c.channel === form.channel,
  );
  const legacyRef =
    form.credential_id.trim() === "" && isSecretRefValid(form.secret_ref);
  const selectedCredential = channelCredentials.find(
    (c) => c.id === form.credential_id,
  );

  /** Save; returns the subscription id or null on failure. */
  const save = async (): Promise<string | null> => {
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        const updated = await deliveryApi.update(form.id, toInput(form));
        return updated.id;
      }
      const created = await deliveryApi.create(projectSlug, toInput(form));
      // From here on this wizard edits, so a retry cannot duplicate.
      patch({ id: created.id });
      return created.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    const id = await save();
    if (id !== null) {
      onSaved();
      onClose();
    }
  };

  const handleSaveAndTest = async (): Promise<void> => {
    const id = await save();
    if (id === null) return;
    onSaved();
    setSaving(true);
    setTestResult(null);
    setSavedButTestFailed(false);
    try {
      setTestResult(await deliveryApi.test(id));
    } catch (err) {
      // The row IS saved; only the dry run failed. A 424 naming a
      // missing credential or stale content is this button's job.
      setSavedButTestFailed(true);
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setSaving(false);
    }
  };

  const scheduleSummary = summarizeSchedule(schedule, t);

  const targetSummary = ((): string => {
    switch (form.channel) {
      case "email":
        return form.recipients;
      case "webhook":
        return form.url;
      case "slack":
        return form.mode === "bot_token"
          ? form.slackChannel
          : t("delivery.channel.slack");
      case "teams":
        return t("delivery.channel.teams");
    }
  })();

  return (
    <Card>
      <CardBody className="space-y-4">
        {/* Step header */}
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-neutral-900">
            {form.id
              ? t("delivery.wizard.titleEdit")
              : t("delivery.wizard.titleNew")}
          </h3>
          <div className="ml-auto flex items-center gap-1 text-xs">
            {STEP_KEYS.map((key, i) => (
              <span key={key} className="flex items-center gap-1">
                {i > 0 && (
                  <ChevronRight size={12} className="text-neutral-300" />
                )}
                <button
                  type="button"
                  // Backward jumps are always allowed; forward only one
                  // step at a time and only when the current one is valid.
                  disabled={i > step && !(i === step + 1 && stepValid(step))}
                  onClick={() => setStep(i)}
                  className={
                    i === step
                      ? "rounded-full bg-accent px-2 py-0.5 font-medium text-white"
                      : "rounded-full px-2 py-0.5 text-neutral-500 disabled:opacity-40"
                  }
                >
                  {i + 1}. {t(key)}
                </button>
              </span>
            ))}
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span className="whitespace-pre-wrap">
              {savedButTestFailed
                ? `${t("delivery.wizard.savedTestFailed")} ${error}`
                : error}
            </span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-auto text-error-dark"
              aria-label={t("common.cancel")}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── Step 1 · What ─────────────────────────────────────────── */}
        {step === 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-neutral-600">
                {t("delivery.field.name")}
              </span>
              <input
                className={inputCls}
                value={form.name}
                aria-label={t("delivery.field.name")}
                placeholder={t("delivery.wizard.namePlaceholder")}
                onChange={(e) => patch({ name: e.target.value })}
              />
            </label>
            <Select
              label={t("delivery.field.report")}
              value={form.report_key}
              onChange={(e) => patch({ report_key: e.target.value })}
              fullWidth
              options={reports.map((r) => ({ value: r.key, label: r.name }))}
            />
            <Select
              label={t("delivery.field.channel")}
              value={form.channel}
              onChange={(e) => changeChannel(e.target.value as DeliveryChannel)}
              fullWidth
              options={CHANNELS.map((c) => ({
                value: c,
                label: t(`delivery.channel.${c}`),
              }))}
            />
            <Select
              label={t("delivery.field.locale")}
              value={form.locale}
              onChange={(e) => patch({ locale: e.target.value })}
              fullWidth
              options={LOCALES.map((l) => ({
                value: l,
                label: t(`delivery.locale.${l}`),
              }))}
            />
          </div>
        )}

        {/* ── Step 2 · To whom ──────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-3">
            {form.channel === "email" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="mb-1 block text-neutral-600">
                    {t("delivery.field.recipients")}
                  </span>
                  <input
                    className={inputCls}
                    value={form.recipients}
                    placeholder="a@example.com, b@example.com"
                    aria-label={t("delivery.field.recipients")}
                    onChange={(e) => patch({ recipients: e.target.value })}
                  />
                  {errors.includes("recipients") && (
                    <span className="mt-1 block text-xs text-error-dark">
                      {t("delivery.error.recipients")}
                    </span>
                  )}
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-neutral-600">
                    {t("delivery.field.subjectPrefix")}
                  </span>
                  <input
                    className={inputCls}
                    value={form.subjectPrefix}
                    aria-label={t("delivery.field.subjectPrefix")}
                    onChange={(e) =>
                      patch({ subjectPrefix: e.target.value })
                    }
                  />
                </label>
              </div>
            )}
            {form.channel === "webhook" && (
              <label className="block text-sm">
                <span className="mb-1 block text-neutral-600">
                  {t("delivery.field.url")}
                </span>
                <input
                  className={`${inputCls} font-mono`}
                  value={form.url}
                  placeholder="https://…"
                  aria-label={t("delivery.field.url")}
                  onChange={(e) => patch({ url: e.target.value })}
                />
                {errors.includes("url") && (
                  <span className="mt-1 block text-xs text-error-dark">
                    {t("delivery.error.url")}
                  </span>
                )}
              </label>
            )}
            {form.channel === "slack" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label={t("delivery.field.mode")}
                  value={form.mode}
                  onChange={(e) => patch({ mode: e.target.value })}
                  fullWidth
                  options={[
                    {
                      value: "incoming_webhook",
                      label: t("delivery.slackMode.incomingWebhook"),
                    },
                    {
                      value: "bot_token",
                      label: t("delivery.slackMode.botToken"),
                    },
                  ]}
                />
                {form.mode === "bot_token" && (
                  <label className="text-sm">
                    <span className="mb-1 block text-neutral-600">
                      {t("delivery.field.slackChannel")}
                    </span>
                    <input
                      className={inputCls}
                      value={form.slackChannel}
                      placeholder="#ops"
                      aria-label={t("delivery.field.slackChannel")}
                      onChange={(e) =>
                        patch({ slackChannel: e.target.value })
                      }
                    />
                    {errors.includes("channel") && (
                      <span className="mt-1 block text-xs text-error-dark">
                        {t("delivery.error.slackChannel")}
                      </span>
                    )}
                  </label>
                )}
              </div>
            )}

            {/* Credential: picked from the Integrations list, never
                typed. `secret_ref` still exists in the API for pre-033
                rows, but no field on this screen produces one. */}
            {REQUIRES_SECRET[form.channel] && (
              <label className="block text-sm">
                <Select
                  label={t("delivery.field.credential")}
                  value={form.credential_id}
                  onChange={(e) => patch({ credential_id: e.target.value })}
                  fullWidth
                  options={[
                    {
                      value: "",
                      label: legacyRef
                        ? t("delivery.credential.legacy")
                        : t("delivery.credential.pick"),
                    },
                    ...channelCredentials.map((c) => ({
                      value: c.id,
                      label: c.secret.configured
                        ? c.name
                        : `${c.name} — ${t("integrations.secret.notConfigured")}`,
                    })),
                  ]}
                />
                {errors.includes("credential") && (
                  <span className="mt-1 block text-xs text-error-dark">
                    {t("delivery.error.credential")}
                  </span>
                )}
                {channelCredentials.length === 0 && !legacyRef && (
                  <span className="mt-2 flex items-center gap-2 rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                    {t("delivery.credential.noneAvailable", {
                      channel: t(`delivery.channel.${form.channel}`),
                    })}
                    <Link
                      to="/admin/integrations"
                      className="shrink-0 font-medium text-accent underline"
                    >
                      {t("delivery.credential.goToIntegrations")}
                    </Link>
                  </span>
                )}
              </label>
            )}

            {form.channel === "teams" && (
              <TeamsWorkflowGuide
                secretRef={form.secret_ref}
                storedInWeb={selectedCredential?.secret.source === "managed"}
              />
            )}
          </div>
        )}

        {/* ── Step 3 · When ─────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" role="radiogroup">
              {(["daily", "weekly", "monthly"] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={schedule.kind === kind}
                  onClick={() => patchSchedule({ kind })}
                  className={
                    schedule.kind === kind
                      ? "rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white"
                      : "rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700"
                  }
                >
                  {t(`delivery.schedule.kind.${kind}`)}
                </button>
              ))}
              {schedule.kind === "custom" && (
                <span className="rounded-lg bg-neutral-100 px-3 py-1.5 text-sm text-neutral-600">
                  {t("delivery.schedule.kind.custom")}
                </span>
              )}
            </div>

            {schedule.kind === "weekly" && (
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_ORDER.map((d) => {
                  const active = schedule.weekdays.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        patchSchedule({
                          weekdays: active
                            ? schedule.weekdays.filter((w) => w !== d)
                            : [...schedule.weekdays, d],
                        })
                      }
                      className={
                        active
                          ? "rounded-full bg-accent px-3 py-1 text-xs font-medium text-white"
                          : "rounded-full border border-neutral-300 px-3 py-1 text-xs text-neutral-600"
                      }
                    >
                      {t(`delivery.weekday.${d}`)}
                    </button>
                  );
                })}
                {errors.includes("weekdays") && (
                  <span className="w-full text-xs text-error-dark">
                    {t("delivery.error.weekdays")}
                  </span>
                )}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              {schedule.kind === "monthly" && (
                <label className="text-sm">
                  <span className="mb-1 block text-neutral-600">
                    {t("delivery.schedule.monthDay")}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    className={inputCls}
                    value={schedule.monthDay}
                    aria-label={t("delivery.schedule.monthDay")}
                    onChange={(e) =>
                      patchSchedule({ monthDay: Number(e.target.value) })
                    }
                  />
                  {schedule.monthDay >= 29 && (
                    <span className="mt-1 block text-xs text-neutral-500">
                      {t("delivery.schedule.monthDayHint")}
                    </span>
                  )}
                </label>
              )}
              {schedule.kind !== "custom" && (
                <label className="text-sm">
                  <span className="mb-1 block text-neutral-600">
                    {t("delivery.schedule.time")}
                  </span>
                  <input
                    type="time"
                    className={inputCls}
                    value={schedule.time}
                    aria-label={t("delivery.schedule.time")}
                    onChange={(e) => patchSchedule({ time: e.target.value })}
                  />
                </label>
              )}
              <Select
                label={t("delivery.field.timezone")}
                value={form.timezone}
                onChange={(e) => patch({ timezone: e.target.value })}
                fullWidth
                options={[
                  // A stored zone outside the curated list is offered as
                  // itself, never dropped or rewritten.
                  ...(TIMEZONES.some((z) => z.id === form.timezone)
                    ? []
                    : [{ value: form.timezone, label: form.timezone }]),
                  ...TIMEZONES.map((z) => ({
                    value: z.id,
                    label: timezoneLabel(z.id, locale),
                  })),
                ]}
              />
            </div>

            <p className="text-sm text-neutral-700">
              {scheduleSummary}
              <span className="text-neutral-500">
                {" — "}
                {timezoneLabel(form.timezone, locale)}
              </span>
            </p>

            {/* Advanced: raw cron + engineering knobs */}
            <div className="rounded-lg border border-neutral-200">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700"
                aria-expanded={advancedOpen}
                onClick={() => setAdvancedOpen((o) => !o)}
              >
                {advancedOpen ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                {t("delivery.advanced.title")}
              </button>
              {advancedOpen && (
                <div className="grid gap-3 border-t border-neutral-200 p-3 sm:grid-cols-3">
                  <label className="text-sm">
                    <span className="mb-1 block text-neutral-600">
                      {t("delivery.field.cron")}
                    </span>
                    <input
                      className={`${inputCls} font-mono`}
                      value={form.cron}
                      aria-label={t("delivery.field.cron")}
                      onChange={(e) => {
                        // Typing here takes over: the schedule becomes
                        // custom and the string is stored verbatim.
                        setSchedule((s) => ({
                          ...s,
                          kind: "custom",
                          rawCron: e.target.value,
                        }));
                        patch({ cron: e.target.value });
                      }}
                    />
                    {errors.includes("cron") && (
                      <span className="mt-1 block text-xs text-error-dark">
                        {t("delivery.error.cron")}
                      </span>
                    )}
                    <span className="mt-1 block text-xs text-neutral-500">
                      {t("delivery.advanced.cronHint")}
                    </span>
                  </label>
                  <label className="text-sm">
                    <span className="mb-1 block text-neutral-600">
                      {t("delivery.field.maxContentAge")}
                    </span>
                    <input
                      type="number"
                      min={1}
                      className={inputCls}
                      value={form.max_content_age_days}
                      aria-label={t("delivery.field.maxContentAge")}
                      onChange={(e) =>
                        patch({
                          max_content_age_days: Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <Select
                    label={t("delivery.field.onMissingContent")}
                    value={form.on_missing_content}
                    onChange={(e) =>
                      patch({
                        on_missing_content: e.target.value as
                          | "fail"
                          | "trigger_watcher",
                      })
                    }
                    fullWidth
                    options={[
                      { value: "fail", label: t("delivery.onMissing.fail") },
                      {
                        value: "trigger_watcher",
                        label: t("delivery.onMissing.triggerWatcher"),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 4 · Review & test ────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-3">
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-neutral-500">
                  {t("delivery.field.report")}
                </dt>
                <dd className="font-medium text-neutral-800">
                  {reports.find((r) => r.key === form.report_key)?.name ??
                    form.report_key}
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">
                  {t("delivery.field.channel")}
                </dt>
                <dd className="font-medium text-neutral-800">
                  {t(`delivery.channel.${form.channel}`)}
                  {targetSummary && (
                    <span className="ml-1 font-normal text-neutral-600">
                      · {targetSummary}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">
                  {t("delivery.wizard.step.when")}
                </dt>
                <dd className="font-medium text-neutral-800">
                  {scheduleSummary}
                  <span className="ml-1 font-normal text-neutral-600">
                    · {timezoneLabel(form.timezone, locale)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-neutral-500">
                  {t("delivery.field.credential")}
                </dt>
                <dd className="font-medium text-neutral-800">
                  {selectedCredential?.name ??
                    (legacyRef
                      ? t("delivery.credential.legacy")
                      : REQUIRES_SECRET[form.channel]
                        ? "—"
                        : t("delivery.credential.notNeeded"))}
                </dd>
              </div>
            </dl>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                aria-label={t("delivery.field.enabled")}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              <span className="text-neutral-700">
                {t("delivery.field.enabled")}
              </span>
            </label>
            {!form.enabled && (
              <p className="text-xs text-neutral-500">
                {t("delivery.review.enabledHint")}
              </p>
            )}

            {testResult && (
              <div className="space-y-2 rounded-lg border border-neutral-200 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-800">
                  <FlaskConical size={14} />
                  {t("delivery.test.title", {
                    date: testResult.as_of_date,
                    locale: testResult.locale,
                  })}
                  <Badge variant="success" size="sm">
                    {t("delivery.review.testOk")}
                  </Badge>
                </div>
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-3 text-xs text-neutral-700">
                  {testResult.rendered_markdown}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 border-t border-neutral-100 pt-3">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <div className="ml-auto flex gap-2">
            {step > 0 && (
              <Button
                ghost
                size="sm"
                onClick={() => setStep((s) => s - 1)}
              >
                {t("delivery.wizard.back")}
              </Button>
            )}
            {step < 3 && (
              <Button
                size="sm"
                disabled={!stepValid(step)}
                onClick={() => setStep((s) => s + 1)}
              >
                {t("delivery.wizard.next")}
              </Button>
            )}
            {step === 3 && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canSave}
                  onClick={() => void handleSaveAndTest()}
                  leftIcon={<FlaskConical size={14} />}
                >
                  {saving ? <Spinner size={14} /> : t("delivery.wizard.saveAndTest")}
                </Button>
                <Button
                  size="sm"
                  disabled={!canSave}
                  onClick={() => void handleSave()}
                >
                  {saving ? <Spinner size={14} /> : t("common.save")}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default DeliveryWizard;
